import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Form, Formik } from "formik";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { Check, RotateCcw, Search, ShieldCheck, X } from "lucide-react";
import {
  RolesListAction,
  UsersListAction,
} from "../../redux/actions/general-action";
import {
  updatePermissionsAction,
  userPermissionsAction,
} from "../../redux/actions/user-action";
import successToast from "../helper-components/success-toast";
import { formatString } from "../helper-components/helper-function";

const MODULES_WITH_DEFAULT_FULL_ACCESS = [
  { name: "price list"},
  // Tray Management — its routes check create / read / update / delete separately.
  { name: "tray" },
];

// The columns defaulted for every full-access module.
const DEFAULT_FULL_ACCESS_HEADINGS = ["create", "update", "read","delete"];

// "Edit Discount" lets a user change the invoice discount of a finished bill.
// It is billing-only: its checkbox shows on the Billing (invoice) row alone,
// and that row's "All" toggle never grants or clears it.
const EDIT_DISCOUNT = "edit_discount";
const EDIT_DISCOUNT_MODULE = "invoice";

const isHeadingShownFor = (moduleName, heading) =>
  heading !== EDIT_DISCOUNT || moduleName === EDIT_DISCOUNT_MODULE;

const normalizeModuleName = (name) =>
  name
    ?.toString()
    ?.trim()
    ?.toLowerCase()
    ?.replace(/_/g, " ");

const getFullAccessConfig = (moduleName) => {
  const normalized = normalizeModuleName(moduleName);
  const entry = MODULES_WITH_DEFAULT_FULL_ACCESS.find((item) => {
    const entryName = typeof item === "string" ? item : item?.name;
    return normalizeModuleName(entryName) === normalized;
  });
  if (!entry) return null;
  const includeDelete = typeof entry === "object" && entry?.delete === true;
  return {
    headings: includeDelete
      ? [...DEFAULT_FULL_ACCESS_HEADINGS, "delete"]
      : DEFAULT_FULL_ACCESS_HEADINGS,
  };
};

const hasDefaultFullAccess = (moduleName) =>
  !!getFullAccessConfig(moduleName);

// Drops the "picklist" permission row (wherever it's nested) when the
// picklist feature is disabled via ruleBook.ts, so it can't be granted/edited
// while the feature itself is hidden from the app.
const removePicklistPermission = (items) =>
  (items || [])
    .filter((item) => item?.module_name !== "picklist")
    .map((item) =>
      item?.hasItems?.length
        ? { ...item, hasItems: removePicklistPermission(item.hasItems) }
        : item
    );

// Same idea as removePicklistPermission, but for the "droplist" module —
// dropped when the drop list feature is disabled via ruleBook.ts.
const removeDropListPermission = (items) =>
  (items || [])
    .filter((item) => item?.module_name !== "droplist")
    .map((item) =>
      item?.hasItems?.length
        ? { ...item, hasItems: removeDropListPermission(item.hasItems) }
        : item
    );

const applyDefaultPermissionValues = (permission) => {
  const headings = getFullAccessConfig(permission?.module_name)?.headings || [];
  if (typeof permission.read !== "boolean") permission.read = true;
  if (typeof permission.create !== "boolean")
    permission.create = headings.includes("create");
  if (typeof permission.update !== "boolean")
    permission.update = headings.includes("update");
  if (typeof permission.delete !== "boolean")
    permission.delete = headings.includes("delete");
  return permission;
};

// Maps each leaf permission_id to the module_name of its top-level (root) entry —
// the same value passed down as `modulename` while rendering — so submit-time logic
// can tell which columns were actually shown for that row (mirrors the JSX below).
const buildRootModuleNameMap = (permissions) => {
  const map = new Map();
  const traverse = (item, rootModuleName) => {
    if (item?.hasItems && item.hasItems.length > 0) {
      item.hasItems.forEach((child) => traverse(child, rootModuleName));
    } else if (item?.permission_id) {
      map.set(item.permission_id, rootModuleName);
    }
  };
  (permissions || []).forEach((root) => traverse(root, root?.module_name));
  return map;
};

// The checkbox columns actually rendered for a given leaf, matching the JSX gating below:
// under "settings", only full-access-listed modules show anything beyond the "All" toggle.
const getVisibleHeadings = (permission, rootModuleName) => {
  if (rootModuleName === "settings") {
    return getFullAccessConfig(permission?.module_name)?.headings || [];
  }
  return tableHeading
    .slice(1)
    .filter((heading) => isHeadingShownFor(permission?.module_name, heading));
};

// Before saving: a checked "All" grants every boolean flag (even ones with no checkbox,
// like a legacy "clone" flag) except Edit Discount, which follows its own checkbox.
// Otherwise, only checkboxes that are both displayed and checked stay true — anything
// hidden or left unchecked is forced to false.
const normalizePermissionForSubmit = (permission, rootModuleName) => {
  const normalized = { ...permission };
  const allChecked = permission?.all === true;
  const visibleHeadings = getVisibleHeadings(permission, rootModuleName);
  Object.keys(permission || {}).forEach((key) => {
    if (key === "all" || typeof permission[key] !== "boolean") return;
    normalized[key] =
      allChecked && key !== EDIT_DISCOUNT
        ? true
        : visibleHeadings.includes(key) && permission[key] === true;
  });
  return normalized;
};

// ---------------------------------------------------------------------------
// Tree helpers. Permissions come as a tree (module -> hasItems -> hasItems);
// only leaves carry flags. A leaf is addressed by its Formik path, e.g.
// "permissions.4.hasItems.0.hasItems.2".
// ---------------------------------------------------------------------------

// Keys on a leaf that are data, never permission flags.
const META_KEYS = [
  "permission_id",
  "module_name",
  "update_date",
  "user_id",
  "created_date",
  "module",
  "main_module_name",
  "module_id",
  "parent_main_module_id",
  "main_module_id",
];

const isFlagKey = (leaf, key) =>
  key !== "all" && !META_KEYS.includes(key) && typeof leaf?.[key] === "boolean";

// Every leaf with its path, its root module and the sub-group it sits in.
const collectLeaves = (tree) => {
  const out = [];
  (tree || []).forEach((root, rootIndex) => {
    const walk = (node, path, trail) => {
      if (node?.hasItems?.length) {
        node.hasItems.forEach((child, i) =>
          walk(child, `${path}.hasItems.${i}`, [...trail, node.module_name])
        );
      } else if (node) {
        out.push({
          path,
          leaf: node,
          rootIndex,
          rootName: root?.module_name,
          group: trail.slice(1).map(formatString).join(" › "),
        });
      }
    };
    walk(root, `permissions.${rootIndex}`, []);
  });
  return out;
};

// Returns a new tree with fn(leaf, path, rootName) applied to every leaf.
const mapLeaves = (tree, fn) =>
  (tree || []).map((root, rootIndex) => {
    const walk = (node, path) =>
      node?.hasItems?.length
        ? {
            ...node,
            hasItems: node.hasItems.map((child, i) =>
              walk(child, `${path}.hasItems.${i}`)
            ),
          }
        : fn(node, path, root?.module_name);
    return walk(root, `permissions.${rootIndex}`);
  });

// Action columns shown for a leaf (the "All" toggle is separate).
const columnsFor = (leaf, rootName) => getVisibleHeadings(leaf, rootName);

// "All" means every flag except Edit Discount is on - including flags with no
// checkbox (e.g. a legacy "clone"), same rule as before.
const isFullAccess = (leaf, rootName) => {
  const keys = new Set([
    ...Object.keys(leaf || {}).filter((k) => isFlagKey(leaf, k)),
    ...columnsFor(leaf, rootName),
  ]);
  keys.delete(EDIT_DISCOUNT);
  return keys.size > 0 && [...keys].every((k) => leaf?.[k] === true);
};

const withAll = (leaf, checked, rootName) => {
  const next = { ...leaf, all: checked };
  Object.keys(leaf || {}).forEach((k) => {
    if (isFlagKey(leaf, k) && k !== EDIT_DISCOUNT) next[k] = checked;
  });
  columnsFor(leaf, rootName).forEach((k) => {
    if (k !== EDIT_DISCOUNT) next[k] = checked;
  });
  return next;
};

const withFlag = (leaf, key, checked, rootName) => {
  const next = { ...leaf, [key]: checked };
  if (key !== EDIT_DISCOUNT) next.all = checked && isFullAccess(next, rootName);
  return next;
};

// A leaf's flag as the user sees it ("All" implies every column but Edit Discount).
const isOn = (leaf, key) =>
  key === "all"
    ? leaf?.all === true
    : leaf?.[key] === true || (leaf?.all === true && key !== EDIT_DISCOUNT);

const leafChanged = (a, b) => {
  const keys = new Set([
    "all",
    ...Object.keys(a || {}).filter((k) => isFlagKey(a, k)),
    ...Object.keys(b || {}).filter((k) => isFlagKey(b, k)),
  ]);
  return [...keys].some((k) => !!a?.[k] !== !!b?.[k]);
};

// "View only": nothing but Read. Only offered where a Read column exists.
const withViewOnly = (leaf, rootName) =>
  withFlag(withAll(leaf, false, rootName), "read", true, rootName);

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 10,
    borderColor: state.isFocused ? "var(--primary)" : "var(--input-border)",
    boxShadow: state.isFocused ? "0 0 0 3px var(--row-selection-color)" : "none",
    "&:hover": { borderColor: "var(--primary)" },
    fontSize: 14,
    backgroundColor: state.isDisabled ? "var(--row-hover-color)" : "#fff",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 40, borderRadius: 10, overflow: "hidden" }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected
      ? "var(--primary)"
      : state.isFocused
      ? "var(--row-hover-color)"
      : "#fff",
    color: state.isSelected ? "#fff" : "var(--heading-color)",
  }),
  placeholder: (base) => ({ ...base, color: "var(--description-color)" }),
};

// ---------------------------------------------------------------------------
// Authorizations screen
// ---------------------------------------------------------------------------

const PermissionSettings = () => {
  const dispatch = useDispatch();
  const picklistEnabled = useSelector(
    (state) => state.picklistReducer.picklistEnabled
  );
  const dropListEnabled = useSelector(
    (state) => state.picklistReducer.dropListEnabled
  );

  const [rolesList, setRolesList] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [role, setRole] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeRoot, setActiveRoot] = useState(null);
  const [initialValues, setInitialValues] = useState({ permissions: [] });
  const formRef = useRef(null);

  useEffect(() => {
    dispatch(
      RolesListAction(
        (data) =>
          setRolesList(
            data?.data?.map((item) => ({
              label: item?.role_name,
              value: item?.role_name,
            })) || []
          ),
        () => {}
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = (roleName) =>
    dispatch(
      UsersListAction(
        roleName,
        (data) =>
          setUsersList(
            data?.data?.userList?.map((item) => ({
              label: item?.user_name,
              value: item?.user_id,
            })) || []
          ),
        () => setUsersList([])
      )
    );

  // Loads a user's permissions. "All" is derived from the flags so a fully
  // granted feature shows as Full access (it used to load unticked).
  const fetchPermissions = (userId) => {
    setLoading(true);
    dispatch(
      userPermissionsAction(
        { user: userId, search: "" },
        (data) => {
          const picklistFiltered = picklistEnabled
            ? data?.data
            : removePicklistPermission(data?.data);
          const filtered = dropListEnabled
            ? picklistFiltered
            : removeDropListPermission(picklistFiltered);
          const prepared = mapLeaves(filtered || [], (leaf, _path, rootName) => {
            const withDefaults = applyDefaultPermissionValues({ ...leaf });
            return { ...withDefaults, all: isFullAccess(withDefaults, rootName) };
          });
          setInitialValues({ permissions: prepared });
          setActiveRoot((current) =>
            prepared.some((r) => r?.module_name === current)
              ? current
              : prepared[0]?.module_name ?? null
          );
          setLoading(false);
        },
        () => {
          setInitialValues({ permissions: [] });
          setLoading(false);
        }
      )
    );
  };

  const confirmDiscard = () => {
    const form = formRef.current;
    if (!form?.dirty) return true;
    return window.confirm("You have unsaved permission changes. Discard them?");
  };

  const onSubmit = (data) => {
    const rootModuleNameMap = buildRootModuleNameMap(data?.permissions);
    const payload = collectLeaves(data?.permissions).map(({ leaf }) => {
      const {
        created_date,
        update_date,
        main_module_name,
        module,
        parent_main_module_id,
        main_module_id,
        ...rest
      } = normalizePermissionForSubmit(
        leaf,
        rootModuleNameMap.get(leaf.permission_id)
      );
      return rest;
    });
    setSaving(true);
    dispatch(
      updatePermissionsAction(
        payload,
        (res) => {
          setSaving(false);
          successToast(res?.message || "Permissions updated");
          fetchPermissions(user?.value);
        },
        () => setSaving(false)
      )
    );
  };

  return (
    <div className="min-h-[calc(100vh-8rem)] flex flex-col bg-[var(--row-hover-color)]">
      {/* Title + who is being edited */}
      <div className="px-6 py-4 bg-white border-b border-[var(--light-border-color)] flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-xl fw_700 text-[var(--heading-color)]">Authorizations</h2>
          <p className="text-sm text-[var(--description-color)] mt-0.5">
            Pick a role and user, then choose what they can do in each module.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="w-52">
            <span className="block text-xs fw_600 text-[var(--description-color)] mb-1">Role</span>
            <Select
              styles={selectStyles}
              options={rolesList}
              value={role}
              placeholder="Select role"
              isSearchable
              onChange={(opt) => {
                if (!confirmDiscard()) return;
                setRole(opt);
                setUser(null);
                setUsersList([]);
                setInitialValues({ permissions: [] });
                fetchUsers(opt?.value);
              }}
            />
          </label>
          <label className="w-60">
            <span className="block text-xs fw_600 text-[var(--description-color)] mb-1">User</span>
            <Select
              styles={selectStyles}
              options={usersList}
              value={user}
              placeholder={role ? "Select user" : "Select a role first"}
              isSearchable
              isDisabled={!role}
              noOptionsMessage={() => "No users in this role"}
              onChange={(opt) => {
                if (!confirmDiscard()) return;
                setUser(opt);
                setSearchTerm("");
                fetchPermissions(opt?.value);
              }}
            />
          </label>
        </div>
      </div>

      {!user ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center p-10">
          <ShieldCheck size={40} strokeWidth={1.5} className="text-[var(--description-color)]" />
          <div className="mt-3 text-base fw_600 text-[var(--heading-color)]">
            Choose a role and user
          </div>
          <div className="mt-1 text-sm text-[var(--description-color)]">
            Their permissions will show here, module by module.
          </div>
        </div>
      ) : loading ? (
        <div className="flex-1 flex items-center justify-center gap-2 text-sm text-[var(--description-color)]">
          <span className="w-4 h-4 rounded-full border-2 border-[var(--light-border-color)] border-t-[var(--primary)] animate-spin" />
          Loading permissions…
        </div>
      ) : (
        <Formik
          innerRef={formRef}
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
        >
          {({ values, setFieldValue, resetForm }) => (
            <PermissionEditor
              values={values}
              initialValues={initialValues}
              setFieldValue={setFieldValue}
              resetForm={resetForm}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              activeRoot={activeRoot}
              setActiveRoot={setActiveRoot}
              saving={saving}
            />
          )}
        </Formik>
      )}
    </div>
  );
};

export default PermissionSettings;

// Action columns, grouped the way people think about them.
const COLUMN_GROUPS = [
  { label: "View", cols: ["read"] },
  { label: "Edit", cols: ["create", "update", "delete"] },
  { label: "Share", cols: ["print", "export", "send"] },
  { label: "Control", cols: ["approver", EDIT_DISCOUNT] },
];
const COLUMN_LABEL = { approver: "Approve", [EDIT_DISCOUNT]: "Edit Discount" };
const colLabel = (c) => COLUMN_LABEL[c] || formatString(c);

// Square tick in the app's standard checkbox colours: solid primary with a
// white tick when granted, white with the standard input border when not.
const Tile = ({ on, onClick, label }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={on}
    aria-label={label}
    title={label}
    onClick={onClick}
    style={{ borderStyle: "solid" }}
    className={`w-7 h-7 rounded-md border flex items-center justify-center transition-colors ${
      on
        ? "bg-[var(--primary)] border-[var(--primary)] text-[var(--tertiary-color)]"
        : "bg-[var(--tertiary-color)] border-[var(--input-border)] hover:border-[var(--primary)]"
    }`}
  >
    {on && <Check size={15} strokeWidth={3} />}
  </button>
);

const Toggle = ({ on, onClick, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    title={label}
    onClick={onClick}
    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
      on ? "bg-[var(--primary)]" : "bg-[var(--input-border)]"
    }`}
  >
    <span
      className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
        on ? "translate-x-[18px]" : "translate-x-[2px]"
      }`}
    />
  </button>
);

// Granted / total action ticks for a feature (a Settings row with no action
// columns counts as one: its full-access switch).
const tickCount = (leaf, rootName) => {
  const cols = columnsFor(leaf, rootName);
  if (cols.length === 0) return [leaf?.all ? 1 : 0, 1];
  return [cols.filter((c) => isOn(leaf, c)).length, cols.length];
};

const PermissionEditor = ({
  values,
  initialValues,
  setFieldValue,
  resetForm,
  searchTerm,
  setSearchTerm,
  activeRoot,
  setActiveRoot,
  saving,
}) => {
  const tree = useMemo(() => values?.permissions || [], [values?.permissions]);
  const leaves = useMemo(() => collectLeaves(tree), [tree]);
  const initialByPath = useMemo(() => {
    const map = new Map();
    collectLeaves(initialValues?.permissions).forEach((l) =>
      map.set(l.path, l.leaf)
    );
    return map;
  }, [initialValues]);

  const isChanged = (l) => leafChanged(l.leaf, initialByPath.get(l.path));
  const changedCount = leaves.filter(isChanged).length;

  const q = searchTerm.trim().toLowerCase();
  const matches = (l) =>
    formatString(l.leaf?.module_name || "").toLowerCase().includes(q) ||
    l.group.toLowerCase().includes(q) ||
    formatString(l.rootName || "").toLowerCase().includes(q);

  const modules = tree.map((root, i) => {
    const own = leaves.filter((l) => l.rootIndex === i);
    const [on, total] = own.reduce(
      (acc, l) => {
        const [a, b] = tickCount(l.leaf, l.rootName);
        return [acc[0] + a, acc[1] + b];
      },
      [0, 0]
    );
    return {
      name: root?.module_name,
      on,
      total,
      hits: q ? own.filter(matches).length : own.length,
      changed: own.some(isChanged),
    };
  });

  const rows = leaves.filter((l) => (q ? matches(l) : l.rootName === activeRoot));
  const rowPaths = new Set(rows.map((r) => r.path));
  const setRows = (fn) =>
    setFieldValue(
      "permissions",
      mapLeaves(tree, (leaf, path, rootName) =>
        rowPaths.has(path) ? fn(leaf, rootName) : leaf
      )
    );

  // Only the groups/columns some row in view actually has.
  const groups = COLUMN_GROUPS.map((g) => ({
    ...g,
    cols: g.cols.filter((c) =>
      rows.some((r) => columnsFor(r.leaf, r.rootName).includes(c))
    ),
  })).filter((g) => g.cols.length > 0);
  const columns = groups.flatMap((g) => g.cols);

  const toggleColumn = (col) => {
    const applicable = rows.filter((r) => columnsFor(r.leaf, r.rootName).includes(col));
    const allOn = applicable.length > 0 && applicable.every((r) => isOn(r.leaf, col));
    setRows((leaf, rootName) =>
      columnsFor(leaf, rootName).includes(col)
        ? withFlag(
            leaf?.all && col !== EDIT_DISCOUNT ? withAll(leaf, true, rootName) : leaf,
            col,
            !allOn,
            rootName
          )
        : leaf
    );
  };

  const toggleCell = (r, col) =>
    setFieldValue(
      r.path,
      withFlag(
        r.leaf?.all && col !== EDIT_DISCOUNT ? withAll(r.leaf, true, r.rootName) : r.leaf,
        col,
        !isOn(r.leaf, col),
        r.rootName
      )
    );

  const [viewOn, viewTotal] = rows.reduce(
    (acc, r) => {
      const [a, b] = tickCount(r.leaf, r.rootName);
      return [acc[0] + a, acc[1] + b];
    },
    [0, 0]
  );

  // Header cells: group divider on the first column of each group.
  const firstOfGroup = new Set(groups.map((g) => g.cols[0]));
  const divider = "border-l border-[var(--light-border-color)]";

  return (
    <Form className="flex-1 flex flex-col">
      <div className="flex-1 flex items-start gap-5 p-5">
        {/* Modules */}
        <aside className="w-60 shrink-0 sticky top-4">
          <div className="flex items-center gap-2 h-10 px-3 rounded-[10px] border border-[var(--input-border)] bg-white focus-within:border-[var(--primary)] focus-within:ring-2 focus-within:ring-[var(--row-selection-color)]">
            <Search size={15} className="text-[var(--description-color)]" />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
                if (e.key === "Escape") setSearchTerm("");
              }}
              placeholder="Search modules or features"
              className="flex-1 min-w-0 text-sm bg-transparent focus:outline-none placeholder:text-[var(--description-color)]"
            />
            {searchTerm && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearchTerm("")}
                className="text-[var(--description-color)] hover:text-[var(--primary)]"
              >
                <X size={14} />
              </button>
            )}
          </div>
          {/* Module list: plain rows, the selected one clearly marked. Each
              button sets border: none inline - an app-wide button style was
              drawing a dark outline on every item. */}
          <nav className="mt-3 rounded-xl bg-white border border-[var(--light-border-color)] p-1.5 flex flex-col gap-0.5">
            <div className="px-3 pt-1.5 pb-2 text-[11px] fw_600 uppercase tracking-wider text-[var(--description-color)]">
              Modules
            </div>
            {modules
              .filter((m) => m.hits > 0)
              .map((m) => {
                const active = !q && m.name === activeRoot;
                const status =
                  m.on === 0 ? "var(--input-border)" : m.on === m.total ? "var(--success-color)" : "var(--dashboard-warning)";
                return (
                  <button
                    key={m.name}
                    type="button"
                    aria-current={active ? "page" : undefined}
                    onClick={() => {
                      setSearchTerm("");
                      setActiveRoot(m.name);
                    }}
                    style={{ border: "none" }}
                    className={`relative w-full flex items-center gap-2.5 text-left pl-4 pr-2.5 py-2.5 rounded-lg transition-colors ${
                      active
                        ? "bg-[var(--row-selection-color)]"
                        : "bg-transparent hover:bg-[var(--row-hover-color)]"
                    }`}
                  >
                    {active && (
                      <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-[var(--primary)]" />
                    )}
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: status }}
                      title={
                        m.on === 0
                          ? "No access"
                          : m.on === m.total
                          ? "Full access"
                          : "Partial access"
                      }
                    />
                    <span
                      className={`flex-1 min-w-0 truncate text-sm ${
                        active ? "fw_700 text-[var(--primary)]" : "fw_500 text-[var(--heading-color)]"
                      }`}
                    >
                      {formatString(m.name)}
                    </span>
                    {m.changed && (
                      <span
                        className="w-1.5 h-1.5 rounded-full bg-[var(--dashboard-warning)] shrink-0"
                        title="Unsaved changes"
                      />
                    )}
                    <span
                      className={`shrink-0 text-[11px] tabular-nums px-1.5 py-0.5 rounded-md ${
                        active ? "bg-white text-[var(--primary)] fw_600" : "bg-[var(--row-hover-color)] text-[var(--description-color)]"
                      }`}
                    >
                      {m.on}/{m.total}
                    </span>
                  </button>
                );
              })}
          </nav>
        </aside>

        {/* Matrix */}
        <section className="flex-1 min-w-0 flex flex-col rounded-xl bg-white border border-[var(--light-border-color)] overflow-hidden">
          <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-[var(--light-border-color)]">
            <div>
              <h3 className="text-lg fw_700 text-[var(--heading-color)]">
                {q ? "Search results" : formatString(activeRoot || "")}
              </h3>
              <p className="text-xs text-[var(--description-color)] mt-0.5">
                {rows.length} feature{rows.length === 1 ? "" : "s"} · {viewOn} of{" "}
                {viewTotal} granted
              </p>
            </div>
            {rows.length > 0 && (
              <div className="inline-flex p-1 rounded-lg bg-[var(--row-hover-color)] gap-1">
                {[
                  ["No access", (leaf, rn) => withAll(leaf, false, rn)],
                  [
                    "View only",
                    (leaf, rn) =>
                      columnsFor(leaf, rn).includes("read") ? withViewOnly(leaf, rn) : leaf,
                  ],
                  ["Full access", (leaf, rn) => withAll(leaf, true, rn)],
                ].map(([label, fn]) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setRows(fn)}
                    className="h-8 px-3 rounded-md text-[13px] fw_600 text-[var(--heading-color)] hover:bg-white hover:shadow-[0_1px_2px_rgba(16,24,40,0.08)]"
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            {rows.length === 0 ? (
              <div className="py-16 text-center text-sm text-[var(--description-color)]">
                Nothing matches “{searchTerm.trim()}”.{" "}
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="fw_600 text-[var(--primary)] hover:underline"
                >
                  Clear search
                </button>
              </div>
            ) : (
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10 bg-[var(--row-hover-color)]">
                  <tr>
                    <th rowSpan={2} className="text-left align-bottom px-5 pb-3 text-[11px] fw_600 uppercase tracking-wider text-[var(--description-color)] min-w-[200px]">
                      Feature
                    </th>
                    {groups.map((g) => (
                      <th
                        key={g.label}
                        colSpan={g.cols.length}
                        className={`pt-3 pb-1 text-[11px] fw_600 uppercase tracking-wider text-[var(--description-color)] ${divider}`}
                      >
                        {g.label}
                      </th>
                    ))}
                    <th rowSpan={2} className={`align-bottom px-4 pb-3 text-[11px] fw_600 uppercase tracking-wider text-[var(--description-color)] ${divider}`}>
                      All
                    </th>
                  </tr>
                  <tr className="border-b border-[var(--light-border-color)]">
                    {columns.map((col) => (
                      <th
                        key={col}
                        className={`px-3 pb-3 pt-1 ${firstOfGroup.has(col) ? divider : ""}`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleColumn(col)}
                          title={`Grant or remove ${colLabel(col)} for every feature`}
                          className="text-[13px] fw_600 text-[var(--heading-color)] whitespace-nowrap hover:text-[var(--primary)] hover:underline underline-offset-4"
                        >
                          {colLabel(col)}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const cols = columnsFor(r.leaf, r.rootName);
                    const groupLabel = q
                      ? [formatString(r.rootName || ""), r.group].filter(Boolean).join(" › ")
                      : r.group;
                    const prev = rows[i - 1];
                    const prevLabel = prev
                      ? q
                        ? [formatString(prev.rootName || ""), prev.group].filter(Boolean).join(" › ")
                        : prev.group
                      : null;
                    return (
                      <Fragment key={r.path}>
                        {groupLabel && groupLabel !== prevLabel && (
                          <tr>
                            <td
                              colSpan={columns.length + 2}
                              className="px-5 pt-4 pb-1 text-xs fw_600 text-[var(--description-color)]"
                            >
                              {groupLabel}
                            </td>
                          </tr>
                        )}
                        <tr className="border-b border-[var(--row-hover-color)] last:border-b-0 hover:bg-[var(--row-hover-color)] transition-colors">
                          <td className="px-5 py-3 text-sm fw_500 text-[var(--heading-color)]">
                            {(() => {
                              // Same status dot as the sidebar: grey none,
                              // amber partial, green full.
                              const [on, total] = tickCount(r.leaf, r.rootName);
                              const color =
                                on === 0 ? "var(--input-border)" : on === total ? "var(--success-color)" : "var(--dashboard-warning)";
                              return (
                                <span className="flex items-center gap-2.5">
                                  <span
                                    className="w-2 h-2 rounded-full shrink-0"
                                    style={{ backgroundColor: color }}
                                  />
                                  {formatString(r.leaf?.module_name)}
                                </span>
                              );
                            })()}
                          </td>
                          {columns.map((col) => (
                            <td
                              key={col}
                              className={`px-3 py-3 ${firstOfGroup.has(col) ? divider : ""}`}
                            >
                              <div className="flex justify-center">
                                {cols.includes(col) ? (
                                  <Tile
                                    on={isOn(r.leaf, col)}
                                    onClick={() => toggleCell(r, col)}
                                    label={`${colLabel(col)} · ${formatString(r.leaf?.module_name)}`}
                                  />
                                ) : (
                                  <span className="w-7 h-7" />
                                )}
                              </div>
                            </td>
                          ))}
                          <td className={`px-4 py-3 ${divider}`}>
                            <div className="flex justify-center">
                              <Toggle
                                on={!!r.leaf?.all}
                                onClick={() =>
                                  setFieldValue(r.path, withAll(r.leaf, !r.leaf?.all, r.rootName))
                                }
                                label={`Full access to ${formatString(r.leaf?.module_name)}`}
                              />
                            </div>
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          <div className="px-5 py-2.5 border-t border-[var(--light-border-color)] text-xs text-[var(--description-color)]">
            Tip: click a column name to switch it on or off for every feature.
          </div>
        </section>
      </div>

      {/* Save bar - pinned to the bottom of the screen; the data scrolls above it */}
      <div className="sticky bottom-0 z-20 flex items-center justify-between gap-4 px-6 py-3 bg-white border-t border-[var(--light-border-color)] shadow-[0_-6px_16px_-10px_rgba(16,24,40,0.18)]">
        <span className={`text-sm ${changedCount ? "fw_600 text-[var(--dashboard-warning-dark)]" : "text-[var(--description-color)]"}`}>
          {changedCount
            ? `${changedCount} unsaved change${changedCount === 1 ? "" : "s"}`
            : "No unsaved changes"}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={!changedCount || saving}
            onClick={() => resetForm()}
            className="h-10 px-4 inline-flex items-center gap-1.5 rounded-lg border border-[var(--input-border)] bg-white text-sm fw_600 text-[var(--heading-color)] hover:bg-[var(--row-hover-color)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <RotateCcw size={15} /> Discard
          </button>
          <button
            type="submit"
            disabled={!changedCount || saving}
            className="h-10 px-5 rounded-lg text-sm fw_600 text-white bg-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </Form>
  );
};

const tableHeading = [
  "all",
  "create",
  "update",
  "delete",
  "print",
  "export",
  "send",
  "read",
  "approver",
  EDIT_DISCOUNT,
];
